/**
 * 响应式适配工具
 * 使用 rem + px + % + vw 混合方案，主力使用 rem
 * 基准设计稿宽度：1920px（PC端）和 375px（移动端）
 * 支持所有 iPhone 型号的自适应（包括 iPhone 12 Pro、14 Pro Max 等）
 */

// 设计稿基准宽度
const DESIGN_WIDTH = 1920; // PC端设计稿宽度
const DESIGN_WIDTH_MOBILE = 375; // 移动端设计稿宽度（iPhone标准宽度）
const BASE_FONT_SIZE = 16; // 基准字体大小（px）

/**
 * 检测是否为移动设备
 */
function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * 设置根元素字体大小
 */
function setRootFontSize() {
  const docEl = document.documentElement;
  // 优先使用 clientWidth，如果为0则使用 innerWidth
  let clientWidth = docEl.clientWidth || window.innerWidth;
  
  // 处理移动端可能的缩放问题
  if (isMobileDevice() && clientWidth === 0) {
    // 移动端备用方案：使用 screen.width
    clientWidth = window.screen.width || 375;
  }
  
  // 移动端断点（小于等于768px视为移动端）
  const isMobile = clientWidth <= 768;
  
  let fontSize;
  
  if (isMobile) {
    // 移动端：基于375px设计稿进行等比缩放
    // 公式：fontSize = (clientWidth / DESIGN_WIDTH_MOBILE) * BASE_FONT_SIZE
    fontSize = (clientWidth / DESIGN_WIDTH_MOBILE) * BASE_FONT_SIZE;
    
    // 移动端字体大小范围调整，支持所有 iPhone 型号
    // iPhone SE (320px): 约 13.65px
    // iPhone 标准 (375px): 16px
    // iPhone 12 Pro (390px): 约 16.64px
    // iPhone 14 Pro Max (430px): 约 18.35px
    // 允许更大的范围，确保大屏 iPhone 也能正确缩放
    fontSize = Math.max(12, Math.min(20, fontSize));
    
    // 针对超小屏设备（如 iPhone SE）做特殊处理
    if (clientWidth <= 320) {
      fontSize = Math.max(12, fontSize);
    }
  } else {
    // PC端：基于1920px设计稿
    // 公式：fontSize = (clientWidth / DESIGN_WIDTH) * BASE_FONT_SIZE
    fontSize = (clientWidth / DESIGN_WIDTH) * BASE_FONT_SIZE;
    // PC端限制字体大小范围，最小14px，最大20px
    fontSize = Math.max(14, Math.min(20, fontSize));
  }
  
  // 设置根元素字体大小
  docEl.style.fontSize = fontSize + 'px';
  
  // 存储到全局，方便其他地方使用
  window.__ROOT_FONT_SIZE__ = fontSize;
  
  // 调试信息（开发环境）
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Responsive] 屏幕宽度: ${clientWidth}px, 根字体大小: ${fontSize}px`);
  }
}

/**
 * 初始化响应式适配
 */
function initResponsive() {
  // 立即执行一次，确保在页面加载时就能设置正确的字体大小
  setRootFontSize();
  
  // 确保 DOM 加载完成后再设置一次（防止某些情况下获取不到正确的宽度）
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setRootFontSize();
    });
  }
  
  // 页面完全加载后再设置一次（确保所有资源加载完成）
  window.addEventListener('load', () => {
    setRootFontSize();
  });
  
  // 监听窗口大小变化
  let resizeTimer = null;
  window.addEventListener('resize', () => {
    // 防抖处理，避免频繁计算
    if (resizeTimer) {
      clearTimeout(resizeTimer);
    }
    resizeTimer = setTimeout(() => {
      setRootFontSize();
    }, 100);
  });
  
  // 监听设备方向变化（移动端）
  window.addEventListener('orientationchange', () => {
    // 延迟执行，等待浏览器完成方向切换
    setTimeout(() => {
      setRootFontSize();
    }, 200);
  });
  
  // 监听页面可见性变化（从后台切回前台时重新计算）
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      setTimeout(() => {
        setRootFontSize();
      }, 100);
    }
  });
}

/**
 * px 转 rem 的辅助函数
 * @param {number} px - 像素值
 * @returns {string} - rem值
 */
function pxToRem(px) {
  const rootFontSize = window.__ROOT_FONT_SIZE__ || BASE_FONT_SIZE;
  return (px / rootFontSize).toFixed(4) + 'rem';
}

// 导出
export {
  initResponsive,
  setRootFontSize,
  pxToRem
};

